import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import { COLUMNS } from "./mockData";
import "styles/dashboard.css";
import "./securityLevel.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

const COL_ORDER_KEY = "trg-seclevel-colorder";
const DEFAULT_ORDER = COLUMNS.map(c => c.key);

function loadColOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(COL_ORDER_KEY) || "null");
    if (!Array.isArray(saved)) return DEFAULT_ORDER.slice();
    const valid   = saved.filter(k => COLUMNS.some(c => c.key === k));
    const missing = DEFAULT_ORDER.filter(k => !valid.includes(k));
    return valid.concat(missing);
  } catch {
    return DEFAULT_ORDER.slice();
  }
}

function saveColOrder(order) {
  try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(order)); } catch {}
}

function applyFilter(rows, text) {
  if (!text) return rows;
  const q = text.toLowerCase();
  return rows.filter(r =>
    Object.values(r).some(v => v != null && String(v).toLowerCase().includes(q))
  );
}

function applySort(rows, key, dir) {
  const col = COLUMNS.find(c => c.key === key);
  const mul = dir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (col?.type === "num") return (av - bv) * mul;
    return String(av).localeCompare(String(bv)) * mul;
  });
}

function downloadCSV(rows, colOrder) {
  const cols = colOrder.map(k => COLUMNS.find(c => c.key === k)).filter(Boolean);
  const esc  = v => {
    const s = String(v == null ? "" : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [cols.map(c => esc(c.label)).join(",")];
  rows.forEach(r => lines.push(cols.map(c => {
    const val = r[c.key];
    return esc(c.fmt ? c.fmt(val) : val ?? "");
  }).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: "security_level_metrics.csv" });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── SVG icons ──────────────────────────────────────────────────────────────────
const IconExpand = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 9 3 13 7 13"/><polyline points="13 7 13 3 9 3"/>
    <line x1="3" y1="13" x2="7" y2="9"/><line x1="13" y1="3" x2="9" y2="7"/>
  </svg>
);
const IconCSV = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2v8"/><polyline points="5 7 8 10 11 7"/>
    <path d="M3 11v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2"/>
  </svg>
);

// ── Sortable table ─────────────────────────────────────────────────────────────
function SecurityTable({ rows, colOrder, setColOrder, sortKey, sortDir, onSort, tableId, bodyId }) {
  const dragKeyRef = useRef(null);

  const orderedCols = colOrder.map(k => COLUMNS.find(c => c.key === k)).filter(Boolean);

  const handleDragStart = (key) => { dragKeyRef.current = key; };
  const handleDrop = (toKey) => {
    const fromKey = dragKeyRef.current;
    if (!fromKey || fromKey === toKey) return;
    const next = colOrder.slice();
    const from = next.indexOf(fromKey), to = next.indexOf(toKey);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, fromKey);
    setColOrder(next);
    saveColOrder(next);
  };

  return (
    <table className="sl-table" id={tableId}>
      <thead>
        <tr>
          {orderedCols.map(col => (
            <th
              key={col.key}
              style={{ textAlign: col.align, minWidth: col.width }}
              draggable
              title="Click to sort · drag to reorder"
              onDragStart={e => { handleDragStart(col.key); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("sl-drop-target"); }}
              onDragLeave={e => e.currentTarget.classList.remove("sl-drop-target")}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("sl-drop-target"); handleDrop(col.key); }}
              onDragEnd={e => e.currentTarget.classList.remove("sl-dragging")}
              onClick={() => onSort(col.key)}
            >
              <span className="sl-drag-grip" aria-hidden="true">⋮⋮</span>
              {col.label}
              <span className="sl-sort-ind">
                {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody id={bodyId}>
        {rows.map((r, i) => (
          <tr key={r.id ?? i}>
            {orderedCols.map(col => {
              const v    = r[col.key];
              const disp = col.fmt ? col.fmt(v) : (v == null ? "" : String(v));
              return (
                <td key={col.key} style={{ textAlign: col.align }}>
                  {disp}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function SecurityLevelPage() {
  const { selectedAccount } = useAppContext();
  const [theme,      setTheme]      = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [securities, setSecurities] = useState(null);  // null = loading, [] = empty
  const [loading,    setLoading]    = useState(false);
  const [sortKey,    setSortKey]    = useState("security_id");
  const [sortDir,    setSortDir]    = useState("desc");
  const [filterText, setFilterText] = useState("");
  const [colOrder,   setColOrder]   = useState(loadColOrder);
  const [expandOpen, setExpandOpen] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) { setSecurities([]); return; }
    setLoading(true);
    fetch(`${DASHBOARD_API}/security-level?token=${token}&account_id=${aid}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setSecurities(Array.isArray(data) ? data : []))
      .catch(() => setSecurities([]))
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  // Close modal on Escape
  useEffect(() => {
    const handler = e => { if (e.key === "Escape" && expandOpen) setExpandOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [expandOpen]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      const col = COLUMNS.find(c => c.key === key);
      setSortDir(col?.type === "num" ? "desc" : "asc");
      setSortKey(key);
    }
  };

  const rows         = securities ?? [];
  const filteredRows = applyFilter(rows, filterText);
  const sortedRows   = applySort(filteredRows, sortKey, sortDir);

  const hasData = rows.length > 0;

  return (
    <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>
      <Helmet><title>Security Level — Tail Risk Global</title></Helmet>
      <DashboardTopbar />
      <DashboardMenuBar onThemeChange={setTheme} />

      <main className="sl-body">
        <div className="sl-card">
          {/* Hover toolbar — only when there's data */}
          {hasData && (
            <div className="sl-tools">
              <button className="sl-tool" title="Download CSV" onClick={() => downloadCSV(sortedRows, colOrder)}>
                <IconCSV />
              </button>
              <button className="sl-tool" title="Expand" onClick={() => setExpandOpen(true)}>
                <IconExpand />
              </button>
            </div>
          )}

          {/* Card header */}
          <div className="sl-card-header">
            <div className="sl-card-title">Security Level Metrics</div>
            {hasData && (
              <div className="sl-header-right">
                <input
                  className="sl-search"
                  type="text"
                  placeholder="Filter…"
                  autoComplete="off"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                />
                <span className="sl-count">{sortedRows.length} / {rows.length}</span>
              </div>
            )}
          </div>

          {/* Body */}
          {loading ? (
            <div className="sl-empty">Loading…</div>
          ) : !hasData ? (
            <div className="sl-empty">No data available</div>
          ) : (
            <div className="sl-table-wrap">
              <SecurityTable
                rows={sortedRows}
                colOrder={colOrder}
                setColOrder={setColOrder}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </div>
          )}
        </div>
      </main>

      {/* Expand modal */}
      {expandOpen && (
        <div
          className="sl-overlay open"
          onClick={e => { if (e.target === e.currentTarget) setExpandOpen(false); }}
        >
          <div className="sl-dialog">
            <div className="sl-dialog-header">
              <div className="sl-card-title">Security Level Metrics</div>
              <div className="sl-dialog-tools">
                <button className="sl-tool" style={{ opacity: 1, position: "static" }} title="Download CSV" onClick={() => downloadCSV(sortedRows, colOrder)}>
                  <IconCSV />
                </button>
                <button className="sl-close" onClick={() => setExpandOpen(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="sl-table-wrap">
              <SecurityTable
                rows={sortedRows}
                colOrder={colOrder}
                setColOrder={setColOrder}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
